import { getCookie, setCookie } from "cookies-next";
import { IncomingMessage, ServerResponse } from "http";
import { GetServerSidePropsContext } from "next";

import publicConfig from "@/config/public";
import { FALLBACK_LOCATION } from "@/constants/fallbackLocation";
import { Location } from "@/custom-types/Location";
import { isLocation } from "@/utils/isLocation";

import getLocationByIp from "./getLocationByIp";

type OptionsType = Parameters<typeof getCookie>[1] | GetServerSidePropsContext;

const { cookieDomain, cookieDomainCa } = publicConfig;

export const getUserLocationData = async (
  domainCountryCode: string,
  context?: Parameters<typeof getCookie>[1],
): Promise<Location> => {
  let location: Location | undefined = await getUserLocationByCookie(context);

  if (!location) {
    try {
      location = await getUserLocationByIp(context);
      if (location) {
        await setLocationCookie(
          { ...location, isUserLocation: true },
          domainCountryCode,
          context,
        );
      } else {
        throw new Error("failed to get user location");
      }
    } catch {
      location = FALLBACK_LOCATION;
    }
  }

  return {
    ...location,
    countryCode: location.country_code,
    formattedLocation: location.formatted_location,
    geoSlug: location.slug,
  };
};

const getUserLocationByCookie = async (
  context?: OptionsType,
): Promise<Location | undefined> => {
  const locationCookie = await getCookie("leafly-location", context);

  try {
    const json = JSON.parse(String(locationCookie));

    if (isLocation(json)) {
      return json;
    } else {
      return undefined;
    }
  } catch {
    return undefined;
  }
};

const getUserLocationByIp = async (
  context?: OptionsType,
): Promise<Location | undefined> => {
  let ip;

  if (context) {
    ip = getUserIp(context);

    if (ip?.slice(0, 7) === "::ffff:") {
      ip = ip.slice(7);
    }
  }
  return getLocationByIp(ip);
};

const getUserIp = (context?: OptionsType): string | undefined => {
  const xForwardedFor = (context?.req as IncomingMessage)?.headers?.[
    "x-forwarded-for"
  ];

  if (xForwardedFor) {
    return String(xForwardedFor).split(",").pop();
  }

  return (context?.req as IncomingMessage)?.socket?.remoteAddress;
};

const setLocationCookie = async (
  location: Location,
  domainCountryCode: string,
  context?: OptionsType,
): Promise<void> => {
  const domain = domainCountryCode === "CA" ? cookieDomainCa : cookieDomain;

  return setCookie("leafly-location", JSON.stringify(location), {
    domain,
    maxAge: 31536000000,
    path: "/",
    req: context?.req as IncomingMessage,
    res: context?.res as ServerResponse<IncomingMessage>,
  });
};
